import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { CredentialsService } from '../../../services/credentials.service';
import { CancellationReason } from '../../../types/cancellation-reason-type.enum';
import { CredentialCancellationRequest } from '../../../types/credential-cancel-request.type';
import { CredentialsStateService } from '../../../services/credentials-state.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-credentials-cancel-justify',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    NgxMaskDirective,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    InputTextareaModule,
    ProgressSpinnerModule,
    ToastModule,
  ],
  providers: [MessageService, provideNgxMask()],
  template: `
      <div class="dialog-body">
          <ng-template pTemplate="header">
                <div class="card-header">
                <h2><i class="pi pi-building"></i>Justificativa de Cancelamento</h2>
                </div>
            </ng-template>
        <form [formGroup]="formJustify" (ngSubmit)="cancelCredential()">
          <div class="form-content">
            <div class="form-field">
              <label for="cancellationReason" class="field-label">Motivo do Cancelamento</label>
              <p-dropdown
                id="cancellationReason"
                [options]="cancellationReasons"
                optionLabel="description"
                optionValue="value"
                formControlName="cancellationReason"
                placeholder="Selecione"
                styleClass="w-full"
                (onChange)="moveToNextField('cancellationReason','cancellationProcessNumber')"
              ></p-dropdown>
              <small *ngIf="isFieldInvalid('cancellationReason')" class="error-message">
                O Motivo é obrigatório.
              </small>
            </div>

            @if(formJustify.value.cancellationReason === 'ROUBADA' || formJustify.value.cancellationReason === 'PERDIDA'){
            <div class="form-field">
              <label for="cancellationProcessNumber" class="field-label">Número do Processo</label>
              <input
                pInputText
                id="cancellationProcessNumber"
                formControlName="cancellationProcessNumber"
                [maxLength]="12"
                mask="A999999/9999"
                style="text-transform: uppercase"
                (keydown.enter)="moveToNextField('cancellationProcessNumber','bulletimNumber')"
              />
            </div>

            <div class="form-field">
              <label for="bulletimNumber" class="field-label">Número do Boletim de Ocorrência</label>
              <input
                pInputText
                id="bulletimNumber"
                formControlName="bulletimNumber"
                style="text-transform: uppercase"
              />
            </div>
            }

            <div class="form-field">
              <label for="cancellationJustify" class="field-label">Justificativa</label>
              <textarea
                pInputTextarea
                id="cancellationJustify"
                formControlName="cancellationJustify"
                rows="4"
              ></textarea>
              <small *ngIf="isFieldInvalid('cancellationJustify')" class="error-message">
                A justificativa é obrigatória.
              </small>
            </div>

            <div class="dialog-actions">
              <button pButton type="button" label="Cancelar" class="p-button-secondary" (click)="dialogRef.close()" [disabled]="isSubmitting"></button>
              <button pButton type="submit" [disabled]="isSubmitting || !formJustify.valid">
                <span *ngIf="!isSubmitting">Confirmar</span>
                <span *ngIf="isSubmitting" class="spinner-inline">
                  <p-progressSpinner styleClass="spinner" strokeWidth="6"></p-progressSpinner>
                  Processando...
                </span>
              </button>
            </div>
          </div>
        </form>
      </div>

      <p-toast></p-toast>
    `,
  styles: [`
    .dialog-body {
      padding: 8px 0 0;
    }

    .form-content {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .form-field {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .field-label {
      font-size: 0.875rem;
      font-weight: 600;
      color: #374151;
    }

    :host ::ng-deep .p-inputtext,
    :host ::ng-deep .p-dropdown,
    :host ::ng-deep .p-inputtextarea {
      width: 100%;
    }

    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 8px;
    }

    .spinner-inline {
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }

    :host ::ng-deep .spinner {
      width: 18px;
      height: 18px;
    }

    .error-message {
      color: #dc3545;
      font-size: 0.85rem;
    }

    :host ::ng-deep .p-dialog {
      border-radius: 5px;
    }
  `],
})
export class CredentialsCancelJustifyComponent {
  formJustify: FormGroup;
  isSubmitting: boolean = false;

  cancellationReasons = Object.keys(CancellationReason).map((key) => ({
    value: key,
    description: CancellationReason[key as keyof typeof CancellationReason],
  }));

  constructor(
    private credentialsService: CredentialsService,
    private credentialsStateService: CredentialsStateService,
    private messageService: MessageService,
    private fb: FormBuilder,
    public dialogRef: DynamicDialogRef,
    public dialogConfig: DynamicDialogConfig
  ) {
    this.formJustify = this.fb.group({
      credentialId: [this.dialogConfig.data?.credentialId, Validators.required],
      cancellationReason: ['', Validators.required],
      cancellationJustify: ['', Validators.required],
      cancellationProcessNumber: [''],
      bulletimNumber: [''],
    });
  }

  cancelCredential(): void {
    //console.log('CredentialId ', this.formJustify);
      if (this.formJustify.valid && !this.isSubmitting) {
    this.isSubmitting = true;

      const formData: CredentialCancellationRequest = this.formJustify.value;

      this.credentialsService.cancelCredential(formData).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Credencial cancelada',
          });

        // Notifica o cancelamento - CORRIGIDO
        this.credentialsStateService.notifyCredentialCancelled();

          this.dialogRef.close();
        },
        error: (err) => {
         this.isSubmitting = false; // ← Reabilita o botão em caso de erro

          let errorMessage = 'Falha ao Cancelar. Por favor tente novamente.';

          if (err.error && err.error.message) {
            errorMessage = err.error.message;
          } else if (err.status === 400) {
            errorMessage = 'Dados inválidos para cancelamento.';
          } else if (err.status === 404) {
            errorMessage = 'Credencial não encontrada.';
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: errorMessage,
          });
        },
        complete: () => {
          this.isSubmitting = false; // ← Garante que o botão seja reabilitado
        }
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Falha ao Cancelar. Dados inválidos',
      });
      // Marca todos os campos como tocados para mostrar os erros
      Object.keys(this.formJustify.controls).forEach(key => {
        this.formJustify.get(key)?.markAsTouched();
      });
    }
  }

  isFieldInvalid(field: string): boolean {
    const control = this.formJustify.get(field);
    return !!(control?.invalid && (control.touched || control.dirty));
  }

  moveToNextField(currentFieldId: string, nextFieldId: string): void {
    const control = this.formJustify.get(currentFieldId);

    if (control) {
      // Marca o campo como tocado para acionar a validação
      control.markAsTouched();
      control.updateValueAndValidity(); // Atualiza a validade após marcar como tocado
    }

    if (this.isFieldInvalid(currentFieldId)) {
      // Se o campo ainda estiver inválido, mantém o foco e exibe o erro
      const currentField = document.getElementById(
        currentFieldId
      ) as HTMLInputElement;
      currentField.focus();
    } else {
      // Se o campo for válido, foca no próximo campo
      const nextField = document.getElementById(nextFieldId) as HTMLElement;
      nextField?.focus();
    }
  }
}
